import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAppSelector } from '@/redux/store';
import { getMembershipConversionOverlayMessage } from '@/utils/membership-access';

type MembershipConversionOverlayProps = {
  membershipPath: string;
};

export const MembershipConversionOverlay = ({
  membershipPath,
}: MembershipConversionOverlayProps) => {
  const titleId = 'paid-membership-overlay-title';
  const descriptionId = 'paid-membership-overlay-description';
  const [footerOffset, setFooterOffset] = useState(0);
  const serviceInfo = useAppSelector((state) => state.user.ServiceInfo);
  const conversionMessage = getMembershipConversionOverlayMessage(serviceInfo);

  useEffect(() => {
    const updateFooterOffset = () => {
      const footer = document.querySelector('footer');
      if (!footer) {
        setFooterOffset(0);
        return;
      }

      const footerRect = footer.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isFooterVisible = footerRect.top < viewportHeight && footerRect.bottom > 0;
      if (!isFooterVisible) {
        setFooterOffset(0);
        return;
      }

      setFooterOffset(Math.min(viewportHeight - footerRect.top, footerRect.height));
    };

    updateFooterOffset();

    const footer = document.querySelector('footer');
    const intersectionObserver = footer
      ? new IntersectionObserver(updateFooterOffset, {
          threshold: [0, 0.25, 0.5, 0.75, 1],
        })
      : null;

    if (footer) {
      intersectionObserver?.observe(footer);
    }

    window.addEventListener('scroll', updateFooterOffset, true);
    window.addEventListener('resize', updateFooterOffset);

    return () => {
      intersectionObserver?.disconnect();
      window.removeEventListener('scroll', updateFooterOffset, true);
      window.removeEventListener('resize', updateFooterOffset);
    };
  }, []);

  return createPortal(
    <section
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      className="pointer-events-none fixed left-0 right-0 z-[60] flex min-h-[430px] items-end justify-center bg-gradient-to-b from-point-100/0 from-[0%] via-point-100/90 via-[55%] to-point-150 pb-[calc(100px+env(safe-area-inset-bottom))] md:left-[var(--sidebar-width,0px)]"
      role="region"
      style={{ bottom: footerOffset }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 backdrop-blur-[1px] [-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.24)_42%,rgba(0,0,0,1)_100%)] [mask-image:linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.24)_42%,rgba(0,0,0,1)_100%)]"
      />
      <div className="pointer-events-auto relative flex max-w-[calc(100%-32px)] flex-col items-center gap-[16px] text-center">
        <p className="text-point-500 Body1S16" id={titleId}>
          {conversionMessage}
        </p>
        <p className="sr-only" id={descriptionId}>
          무료 사용자는 유료 멤버십 가입 후 셀러박스 주요 기능을 사용할 수 있습니다.
        </p>
        <Link
          className="inline-flex min-h-[44px] items-center justify-center rounded-[8px] bg-point-500 px-[35px] py-[11px] text-center text-white transition-colors Body3S15 hover:bg-point-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-point-400 focus-visible:ring-offset-2"
          to={membershipPath}
        >
          멤버십 가입하기
        </Link>
      </div>
    </section>,
    document.body,
  );
};
